import {
  useMount,
  FONT_SIZE_EXTRA_SMALL,
  INVALID_CUSTOMERS,
} from "constants/constants";
import { fetchCustomerJson } from "../../../../DataGetter/CustomerJsonGetter";
import ComputerIcon from "@mui/icons-material/DesktopWindowsRounded";
import TabletIcon from "@mui/icons-material/Tablet";
import styled from "styled-components/macro";
import { Tooltip } from "@mui/material";
import { CustomerListItem } from "../customerTilesUtils";
import useFlagsWithDefaults from "utils/customHooks/useFlagsWithDefaults";
import { customerInfoJsonAtom } from "reports/utils/hooks/useCustomerInfoJson";
import { useAtom } from "jotai";
import { uniq } from "lodash";
import { useMemo } from "react";
import {
  TOOLTIP_PROPS,
  customerTilesPopupDataAtom,
} from "./internalToolsUtils";
import { allMidgardProcessGroupsAtom } from "./FetchAllMidgardProcessGroups";
import { allWorkSchedulesAtom } from "./FetchAllWorkScheduleConfigs";
import { PunchClock } from "@mui/icons-material";
import { highlight } from "constants/colors";

const CustomerInfoIndicatorStyles = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: absolute;
  bottom: 0px;
  left: 3px;
  right: auto;
  font-size: ${FONT_SIZE_EXTRA_SMALL}px;
  cursor: pointer;
  color: hsl(0, 0%, 50%);
  .groupsAndProcesses {
    padding-left: 0px;
    padding-bottom: 3px;
  }
  .groupsAndProcesses,
  .numGroups,
  .numProcesses {
    display: flex;
    align-items: center;
    height: 100%;
  }
  .numGroups,
  .numProcesses,
  .numWorkSchedules {
    background: rgba(255, 255, 255, 0.8);
  }
  .numGroups {
    padding-left: 0px;
    padding-right: 4px;
  }
  .numProcesses {
    padding-right: 4px;
  }
  .numWorkSchedules {
    padding-right: 1px;
    margin-left: -3px;
    .MuiSvgIcon-root {
      margin-bottom: -1px;
      margin-right: -1px;
    }
  }
  .MuiSvgIcon-root {
    margin-left: 0.1em;
    margin-right: 0.1em;
    width: 0.45em;
    height: 0.45em;
  }
  .btnSearch .MuiSvgIcon-root {
    margin: 0;
  }
  &:hover {
    transition: all 0.2s;
    transform: scale(1.05);
    transform-origin: bottom left;
    box-shadow: 1px -1px 2px rgba(0, 0, 0, 0.12);
  }
`;
const highlightSemiTransparent = highlight + "b3";
export default function CustomerInfoIndicator({ ckey }: { ckey: string }) {
  // grab customer info & machemetry data from LS
  // if it's not there, query for it and store it there

  const [allMidgardProcessGroups] = useAtom(allMidgardProcessGroupsAtom);
  const { numGroups, numProcesses } = useMemo(() => {
    const midgardProcessGroups = allMidgardProcessGroups[ckey] ?? [];
    if (!midgardProcessGroups) {
      return {
        numGroups: 0,
        numProcesses: 0,
      };
    }
    const groups = midgardProcessGroups.filter(group =>
      group?.types?.includes("FLIGHT")
    );
    // remove any duplicate processes
    const processes = groups
      .map(g => g.processes)
      .reduce((a, b) => a.concat(b), []);
    const uniqProcessesNames = uniq(processes.map(p => p.name));
    return {
      numGroups: groups.length,
      numProcesses: uniqProcessesNames.length,
    };
  }, [allMidgardProcessGroups, ckey]);

  const [, setCustomerTilesPopupData] = useAtom(customerTilesPopupDataAtom);
  const [allWorkSchedules] = useAtom(allWorkSchedulesAtom);
  const workSchedules = allWorkSchedules[ckey] ?? [];
  const numWorkSchedules = workSchedules.length;
  return numGroups > 0 || numProcesses > 0 ? (
    <CustomerInfoIndicatorStyles
      onClick={e => {
        e.stopPropagation();
        setCustomerTilesPopupData({
          ckey,
          popupData: {
            processGroups: allMidgardProcessGroups[ckey] ?? {},
            workSchedules: allWorkSchedules[ckey] ?? [],
          },
          adminUrls: [
            {
              url: `https://portal.raven.ai/${ckey}/processgroups`,
              name: "Process Groups",
            },
            {
              url: `https://${ckey}.gke-us-east1.raven-cloud.com/midgard/admin/deployment_config/workscheduleconfig/`,
              name: "Work Schedule Config",
            },
          ],
        });
      }}
    >
      <Tooltip
        title={`🔍 ${numGroups} group${
          numGroups === 1 ? "" : "s"
        }, ${numProcesses} process${numProcesses === 1 ? "" : "es"}${
          numWorkSchedules > 1 ? `, ${numWorkSchedules} work schedules` : ""
        }`}
        {...TOOLTIP_PROPS}
      >
        <div className="groupsAndProcesses">
          <div
            className="numGroups"
            style={numGroups > 9 ? { color: highlightSemiTransparent } : {}}
          >
            <ComputerIcon /> {numGroups}
          </div>
          <div
            className="numProcesses"
            style={numProcesses > 9 ? { color: highlightSemiTransparent } : {}}
          >
            <TabletIcon /> {numProcesses}
          </div>
          {numWorkSchedules > 1 ? (
            <div
              className="numWorkSchedules"
              style={
                numWorkSchedules > 9 ? { color: highlightSemiTransparent } : {}
              }
            >
              <PunchClock /> {numWorkSchedules}
            </div>
          ) : null}
        </div>
      </Tooltip>
    </CustomerInfoIndicatorStyles>
  ) : null;
}

/** if you're on an internal url & you're a raven user,
 * populate your localStorage with customerjson & machemetry data,
 * which we can show on the tiles
 */
// tslint:disable-next-line: cognitive-complexity
export function usePopulateCustomerJsonsInAtom(details: CustomerListItem[]) {
  const { flightMagicStringEnabled } = useFlagsWithDefaults();
  // customer json
  const [allCustomerInfoJsons, setAllCustomerInfoJsons] =
    useAtom(customerInfoJsonAtom);
  useMount(() => {
    let nextAllCustomerInfoJsons = { ...allCustomerInfoJsons };
    Promise.allSettled(
      details.map(({ abbrevName: ckey }) => {
        const jsonForThisCustomer = allCustomerInfoJsons[ckey];

        if (
          jsonForThisCustomer ||
          !ckey ||
          (ckey === "raven-employees" && flightMagicStringEnabled) ||
          INVALID_CUSTOMERS.includes(ckey)
        ) {
          return Promise.resolve();
        }

        return fetchCustomerJson(ckey).then(data => {
          nextAllCustomerInfoJsons = {
            ...nextAllCustomerInfoJsons,
            [ckey]: data,
          };
        });
      })
    ).then(() => {
      setAllCustomerInfoJsons(prev => ({
        ...prev,
        ...nextAllCustomerInfoJsons,
      }));
    });
  });
}
